/**
 * SuppressionModal Component
 *
 * Modal for suppressing AI-generated insights or deleting manual items.
 * Collects feedback for AI learning when suppressing AI insights.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SuppressionModalState } from '../types';

interface SuppressionModalProps {
  state: SuppressionModalState;
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}

export default function SuppressionModal({
  state,
  onConfirm,
  onCancel,
}: SuppressionModalProps) {
  const { t } = useTranslation('people');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!state.isOpen) return null;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(reason);
      setReason('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAiItem = state.itemSource === 'email_insights';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isAiItem ? t('suppression_modal.title_suppress') : t('suppression_modal.title_delete')}
              </h3>
              <p className="text-sm text-gray-500">
                {isAiItem
                  ? t('suppression_modal.subtitle_suppress')
                  : t('suppression_modal.subtitle_delete')}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Item being suppressed */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              {state.itemType === 'expertise' ? t('suppression_modal.expertise_label') : t('suppression_modal.fact_label')} {t('suppression_modal.to_remove')}:
            </p>
            <p className="text-gray-900 font-medium">{state.itemContent}</p>
          </div>

          {/* Reason input (only for AI-extracted items) */}
          {isAiItem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('suppression_modal.reason_label')} <span className="text-gray-400 font-normal">({t('suppression_modal.helps_ai')})</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('suppression_modal.reason_placeholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-200 focus:border-amber-400 transition-colors resize-none"
                rows={3}
                autoFocus
              />
              <p className="mt-1.5 text-xs text-gray-500">
                {t('suppression_modal.feedback_note')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('suppression_modal.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
              isAiItem
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isSubmitting ? t('suppression_modal.processing') : isAiItem ? t('suppression_modal.suppress_and_save') : t('suppression_modal.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
